import {
  ConversationFlow,
  ICharacter,
  IConversationMessage,
  IUpdateMessageRequest,
  IUpdateMessageReturn,
  TypingMode,
} from "@next-platform/client-common-core";
import { IconVariants } from "@next-platform/client-common-spicy-ui";
import React, { type Dispatch, SetStateAction } from "react";

import { GenerateResult, GenerationOptions, IRequestErrorHandler } from "@/pages/Chat/hooks/types";

import { ChatPageModal } from "../constants";
import { IhandlePreferredVoiceActions } from "../voices/types";

export enum ModalType {
  TRAINING = "Training",
  PREMIUM_TRAINING = "PremiumTraining",
  PREMIUM_ACCESS = "PremiumAccess",
}

export enum MessageType {
  TEXT = "Text",
  IMAGE = "Image",
}

export interface MessageRatingProps {
  messageId?: string;
  rating?: string;
  sendRating?: (messageId: string, label: string) => Promise<void> | void;
  messageType?: MessageType;
}
export interface ChatBubbleEditBoxProps {
  value: string | undefined;
  onChange: (val: string) => void;
  onCancel: () => void;
  onSettings?: () => void;
  onSave: () => void;
  isSaving: boolean;
  isImageEditMode?: boolean;
}

export interface IChatBarActions {
  icon: IconVariants;
  isDisabled?: boolean;
  isHidden?: boolean;
  isFilled?: boolean;
  isLoading?: boolean;
  className?: string;
  onPress?: () => void;
  key: string;
  testId?: string;
  classNames?: {
    icon?: string;
  };
  title: string;
}

export interface ChatBarProps {
  onClickPrevious?: () => void;
  onClickNext?: () => void;
  count?: number;
  character: ICharacter | null;
  message?: IConversationMessage;
  isTyping?: TypingMode | null;
  allowRegenerate?: boolean;
  onRegenerate: (props?: GenerationOptions) => Promise<GenerateResult>;
  conversationLength: number;
  chatIndex?: number;
  altMsgs?: IConversationMessage[];
  isClonePartial: boolean;
  removalMode: boolean;
  loadMessages: () => Promise<void>;
  currentConversationId: string | null;
  voiceId: string;
  handlePreferredVoiceActions: (args: Partial<IhandlePreferredVoiceActions>) => Promise<void>;
  isMessageEditMode?: boolean;
  setIsMessageEditMode: Dispatch<React.SetStateAction<boolean>>;
  sendRating: (messageId: string, label: string) => Promise<void>;
  setCharacterThatIsTyping: (subCharacter: ICharacter | null) => void;
  currentAudioMessageId: string;
  defaultPersonaId: string | null;
  isAudioLoading: boolean;
  handleModalVisibility: (modal: ChatPageModal, visible: boolean) => void;
  handleVoiceButtonClick: (messageId: string) => void;
  showVoiceSelectionModal: boolean;
  scrollToBottom: () => void;
  messageEditMode?: "text" | "image" | null;
  setMessageEditMode?: Dispatch<React.SetStateAction<"text" | "image" | null>>;
  setEditMessageContent?: Dispatch<React.SetStateAction<string | undefined>>;
  isChatWithGroup?: boolean;
  hasPendingMessage?: boolean;
  isGroupBelowMinimum?: boolean;
  isLastMessage?: boolean;
}

export interface ChatBubbleProps {
  character: ICharacter;
  message: IConversationMessage;
  user_name: string;
  user_avatar_url?: string;
  isTyping?: TypingMode | null;
  allowRegenerate?: boolean;
  onRegenerate: (props?: GenerationOptions) => Promise<GenerateResult>;
  isSelectedForRemoval: boolean;
  onRemovalSelected: Dispatch<SetStateAction<number | null>>;
  setConversation: Dispatch<SetStateAction<IConversationMessage[]>>;
  currentConversationId: string | null;
  createConversation?: (args: Pick<IConversationMessage, "role" | "content">[]) => Promise<void>;
  removalMode: boolean;
  loadMessages?: () => Promise<void>;
  altMsgs?: IConversationMessage[];
  chatIndex: number;
  isClonePartial: boolean;
  isSelectedForClone: boolean;
  onCloneSelected: (index: number) => void;
  conversationLength: number;
  isRemoveMessageLoading: boolean;
  handleFlagModalOpen: (id: string, isAltMsg: boolean) => void;
  voiceId: string;
  infoMessage: {
    messageId?: string;
    message?: string;
  };
  handlePreferredVoiceActions: (args: Partial<IhandlePreferredVoiceActions>) => Promise<void>;
  showCheckbox: boolean;
  requestErrorHandler?: IRequestErrorHandler;
  isMessageEditMode: boolean;
  setIsMessageEditMode: Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactElement;
  isChatWithGroup?: boolean;
  handleEditPendingMessage?(content: string): void;
  onUserUpdateMessage: (args: IUpdateMessageRequest) => Promise<IUpdateMessageReturn>;
  isLoading: boolean;
  loadingType: ConversationFlow | null;
  handleDeleteMessageImage: (messageId: string) => Promise<void>;
  /** Opens the details of the gallery file behind the message image; absent where a chat cannot open them. */
  onOpenImageDetails?: (mediaFileId: string) => void;
  isImageDeleteFromMessageLoading: boolean;
}

export enum ChatBubbleVariants {
  USER = "user",
  BOT = "bot",
  CLONE = "clone",
  DELETE = "delete",
}

export interface ChatBubbleRatingProps {
  isSubmitting?: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  setLabelSentMessage: (labelSentMessage: string) => void;
  messageRating?: MessageRatingProps;
  setShowRatingBox: (showRatingBox: boolean) => void;
  setIsRatingSent: (isRatingSent: boolean) => void;
}

export type NsfwGateCta = "paywall" | "verifyAge" | "disabled";

export interface ChatBubbleContentProps {
  message: IConversationMessage | null;
  userName: string;
  characterName: string;
  isAvatarImageBlurred: boolean;
  isSelected?: boolean;
  showCheckbox?: boolean;
  isImageNsfwGatingEnabled?: boolean;
  isTextNsfwGatingEnabled?: boolean;
  textNsfwCtaMode: NsfwGateCta | undefined;
  imageNsfwCtaMode: NsfwGateCta | undefined;
  /** Set only for a message that names its gallery file; an older message keeps the plain zoom. */
  onOpenImageDetails?: () => void;
}

export enum VoiceButtonVariant {
  PLAY = "Play",
  STOP = "Stop",
  IDLE = "Idle",
}

export enum VoiceUpgradeVariant {
  GUEST = "Guest",
  MEMBER = "Member",
}

export enum AutoResponseUpgradeVariant {
  GUEST = "Guest",
  PREMIUM = "Premium",
}

export enum ChatActions {
  RATE_MESSAGE = "RateMessage",
  REGENERATE = "Regenerate",
  CONTINUE_CHAT = "ContinueChat",
  GENERATE_IMAGE = "GenerateImage",
  VOICE = "Voice",
  EDIT = "Edit",
}

export enum ChatRatings {
  TERRIBLE = "Terrible",
  BAD = "Bad",
  GREAT = "Great",
  FANTASTIC = "Fantastic",
}
